import { holidayOn } from '@progear/finance-core';
import { cn } from '@/lib/utils';
import { formatEur, formatEurCompact, pickSum } from '@/lib/cashFlow';
import { useCalendar } from './context';
import { DayEntries } from './DayEntries';
import { MONTHS } from './model';

/** The bottom running-balance pill: opening → closing, always bruto (real cash). */
function BalancePill({
  opening,
  closing,
  grossMove,
  onOpen,
}: {
  opening: number;
  closing: number;
  grossMove: number;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      title="Saldo bruto — arvestuslik jooksev saldo (algsaldost edasi arvutatud)"
      className="mt-auto flex w-full items-stretch gap-1 rounded-b border-t border-border/60 pt-0.5 text-left transition-colors hover:bg-accent/40"
    >
      <span
        aria-hidden
        title="Saldo on alati bruto (reaalne raha), olenemata neto/bruto valikust"
        className="shrink-0 self-center rounded border border-[hsl(var(--vatmode))]/45 bg-[hsl(var(--vatmode))]/12 px-0.5 text-[7px] font-semibold uppercase leading-tight tracking-wide text-[hsl(var(--vatmode))]"
      >
        B
      </span>
      <span className="flex min-w-0 flex-1 items-baseline justify-between gap-0.5 overflow-hidden pr-1">
        <span className="shrink-0 text-[6px] uppercase tracking-wide text-muted-foreground/45">alg</span>
        <span className="truncate tabular-nums text-[9px] text-muted-foreground/55">{formatEurCompact(opening)}</span>
      </span>
      <span className="flex min-w-0 flex-1 items-baseline justify-between gap-0.5 overflow-hidden border-l border-border/50 pl-1">
        <span className="flex shrink-0 items-center gap-0.5 text-[6px] uppercase tracking-wide text-muted-foreground/70">
          lõpp
          {grossMove !== 0 && (
            <span
              aria-hidden
              className={cn(
                'text-[9px] leading-none',
                grossMove > 0 ? 'text-[hsl(var(--income))]' : 'text-[hsl(var(--cost))]',
              )}
            >
              {grossMove > 0 ? '↑' : '↓'}
            </span>
          )}
        </span>
        <span
          className={cn(
            'truncate tabular-nums text-[9px] font-medium',
            closing < 0
              ? 'text-[hsl(var(--cost))]'
              : grossMove !== 0
                ? 'text-foreground/90'
                : 'text-muted-foreground/70',
          )}
        >
          {formatEurCompact(closing)}
        </span>
      </span>
    </button>
  );
}

/**
 * A single calendar day. Drop-target for drag-to-reschedule, host for the day's
 * chips, the Σ day-change button (opens the register) and the running-balance
 * pill. A null date renders an empty grid pad; a date with no rollup (outside the
 * window) renders dimmed. Holidays get a faint hatch + their name. All numbers
 * are finance-core truth.
 */
export function DayCell({ date }: { date: string | null }) {
  const ctx = useCalendar();
  if (date === null) {
    return <div className="min-h-24 bg-background/30" />;
  }

  const { mode, today, model, arrangeDay, overDate } = ctx;
  const m0 = Number(date.slice(5, 7)) - 1;
  const day = Number(date.slice(8, 10));
  const rollup = model.rollupByDate.get(date);
  const raw = model.entriesByDate.get(date) ?? [];
  const dayEntries = arrangeDay(raw);
  const isToday = date === today;
  const future = date > today;
  const isOver = overDate === date;
  const net = rollup ? pickSum(rollup.net, mode) : 0;
  const grossMove = rollup ? rollup.net.gross : 0;
  const opening = model.openingByDate.get(date) ?? 0;
  const hol = holidayOn(date)?.name;

  return (
    <div
      data-date={date}
      data-now={isToday ? '1' : undefined}
      onDragOver={(ev) => ctx.dayDragOver(date, ev)}
      onDragLeave={() => ctx.dayDragLeave(date)}
      onDrop={(ev) => ctx.dayDrop(date, ev)}
      className={cn(
        'group relative flex min-h-24 flex-col gap-0.5 p-1.5 transition-colors',
        !rollup && 'opacity-40',
        isOver
          ? 'bg-[hsl(var(--primary))]/10 ring-1 ring-inset ring-[hsl(var(--primary))]/70'
          : isToday
            ? 'bg-background ring-1 ring-inset ring-[hsl(var(--primary))]/40'
            : hol
              ? 'pg-hol-hatch bg-white/[0.015]'
              : 'bg-background',
      )}
    >
      <div className="flex items-center justify-between gap-1">
        <div className="flex min-w-0 items-center gap-1">
          {rollup ? (
            <button
              type="button"
              onClick={() => ctx.openDay(date, net)}
              title={`Ava päev: ${day}. ${MONTHS[m0]}`}
              className={cn(
                'flex h-5 min-w-5 shrink-0 items-center justify-center rounded px-1 text-[11px] transition-colors',
                isToday
                  ? 'bg-[hsl(var(--primary))] font-semibold text-primary-foreground hover:bg-[hsl(var(--primary))]/90'
                  : future
                    ? 'font-semibold text-[hsl(var(--vatmode))] hover:bg-[hsl(var(--vatmode))]/15'
                    : 'text-foreground hover:bg-accent',
              )}
            >
              {day}
            </button>
          ) : (
            <span className="px-1 text-[11px] text-muted-foreground">{day}</span>
          )}
          {hol && (
            <span title={`Riigipüha: ${hol}`} className="min-w-0 truncate text-[9px] italic text-white/70">
              {hol}
            </span>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={() => ctx.addEntry(date)}
            title="Planeeri makse/laekumine sellele päevale"
            className="flex h-4 w-4 items-center justify-center rounded text-[12px] leading-none text-muted-foreground opacity-0 transition-opacity hover:bg-accent hover:text-foreground group-hover:opacity-100"
          >
            +
          </button>
          {rollup && raw.length > 0 && (
            <button
              type="button"
              onClick={() => ctx.openDay(date, net)}
              title="Ava päeva register — näe, millest muutus koosneb"
              className="flex items-center gap-1 rounded border border-border/80 bg-foreground/[0.07] px-1 py-px transition-colors hover:bg-accent"
            >
              <span aria-hidden className="text-[8px] font-semibold text-muted-foreground">
                Σ
              </span>
              <span
                className={cn(
                  'tabular-nums text-[10px] font-medium',
                  net >= 0 ? 'text-[hsl(var(--income))]' : 'text-[hsl(var(--cost))]',
                )}
              >
                {net >= 0 ? '+' : '−'}
                {formatEur(Math.abs(net))}
              </span>
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-0.5">
        <DayEntries date={date} placed={dayEntries} />
      </div>

      {/* The balance pill is the running balance rolled forward from the opening
          anchor (cash truth) — hidden under the accrual basis, like every other
          saldo affordance. */}
      {rollup && !ctx.accrual && (
        <BalancePill
          opening={opening}
          closing={rollup.runningBalance}
          grossMove={grossMove}
          onOpen={() => ctx.openDay(date, net)}
        />
      )}
    </div>
  );
}
